/**
 * Responsive Layout Switcher
 * 
 * Toggles between a grid layout and carousel/slider layout based on screen width.
 * Uses a carousel for tablet devices and grid for desktop.
 *
 * @module breakpointLayoutSwitch
 */
import $ from 'jquery';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import mergeOptions from '../lib/merge-options';
import customPaging from './slick/custom-paging';
import './slick-carousel';

// Constants for plugin configuration
export const PLUGIN_NAME = 'breakpointLayoutSwitch';
export const RESIZE_EVENT = 'resize';
export const SLICK_TRACK = '.js-carousel__track';
export const DEFAULTS = {
    switchBreakpoint: 900, // Width in pixels where the layout will switch
    slidesToShow: 10,      // Number of slides visible at once in carousel mode
    slidesToScroll: 10,    // Number of slides to scroll at once
    infinite: false,       // Whether the carousel loops infinitely
    dotsClass: "carousel__dots",
    appendDots: ".js-carousel-dots",
};

/**
 * Initializes the Slick carousel for smaller screens
 * 
 * @param {jQuery} $el - The container element
 * @param {jQuery} $slickTrack - The element to be transformed into a carousel
 * @param {Object} opts - Configuration options
 */
const setUpSlick = ($el, $slickTrack, opts) => {
    // Remove grid classes if they exist
    // $slickTrack.removeClass('g n-block-grid--5');
    
    // Extract options
    const { slidesToShow, 
            slidesToScroll, 
            infinite, 
            dotsClass, 
            appendDots } = opts;
    
    // Get navigation arrows
    const getPrev = $el.find(".js-carousel__prev");
    const getNext = $el.find('.js-carousel__next');
    
    // Initialize Slick carousel with options
    $slickTrack.slick({
        dots: true,
        customPaging,
        dotsClass,
        appendDots: $(appendDots),
        slidesToShow,
        slidesToScroll,
        infinite,
        nextArrow: getNext,
        prevArrow: getPrev,
    });
};

/**
 * Restores the grid layout for larger screens
 * 
 * @param {jQuery} $el - The container element
 * @param {jQuery} $slickTrack - The element to be transformed back to grid
 * @param {Object} opts - Configuration options
 */
const setUpGrid = ($el, $slickTrack, opts) => {
    // Destroy Slick instance
    $slickTrack.slick('unslick');
    
    // Clean up DOM structure created by Slick
    const gridItems = $slickTrack.find('.js-layout-switch-item');
    gridItems.unwrap().unwrap();
    gridItems.attr('style', '');

    // Add grid classes
    // $slickTrack.addClass('g n-block-grid--5');
};

/**
 * Core function that decides which layout to use based on window width
 * 
 * @param {jQuery} $el - The container element
 * @param {jQuery} $window - jQuery window object
 * @param {Object} opts - Configuration options
 */
const breakpointLayoutSwitchFunc = ($el, $window, opts) => {
    const $slickTrack = $el.find(SLICK_TRACK);
    const { switchBreakpoint } = opts;
    
    // Check if Slick is already initialized
    const slickIsInitialized = $slickTrack.hasClass('slick-initialized');
    
    // Check if window width is below the breakpoint
    const windowMatchesMediaQuery = window.matchMedia(`(max-width: ${switchBreakpoint - 1}px)`).matches;

    // Switch to Slick if below breakpoint and not initialized
    if (windowMatchesMediaQuery && !slickIsInitialized) {
        setUpSlick($el, $slickTrack, opts);
    } 
    // Switch to Grid if above breakpoint and Slick is initialized
    else if (!windowMatchesMediaQuery && slickIsInitialized) {
        setUpGrid($el, $slickTrack, opts);
    }
};

/**
 * Sets up window resize event listener
 * 
 * @param {jQuery} $el - The container element
 * @param {jQuery} $window - jQuery window object
 * @param {Object} opts - Configuration options
 */
const applyEventHandlers = ($el, $window, opts = {}) => {
    $window.on(RESIZE_EVENT, () => {
        breakpointLayoutSwitchFunc($el, $window, opts);
    });
};

/**
 * Main initialization function for the breakpointLayoutSwitch plugin
 * 
 * @param {HTMLElement} el - The container element
 * @param {Object} options - Custom configuration options
 * @returns {void}
 */
export default function breakpointLayoutSwitch(el, options = {}) {
    const $el = $(el);
    // Merge default options with custom options
    const opts = mergeOptions(DEFAULTS, options, $el);
    const $window = $(window);
    
    // Initialize layout based on current window size
    breakpointLayoutSwitchFunc($el, $window, opts);
    
    // Set up resize listener to switch layouts when window size changes
    applyEventHandlers($el, $window, opts);
}

// Register as a jQuery plugin
registerJQueryPlugin(PLUGIN_NAME, breakpointLayoutSwitch);


// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/breakpoint-layout-switch.js